import { startCase } from 'lodash';

import type { AggregationColumnItem } from '../../datasets/deliverable-column-config';

/**
 * Extra categories to disable beyond the unit's own name.
 * Every unit of analysis automatically disables its own matching aggregation
 * category; this map adds *additional* categories for special cases.
 */
export const UNIT_EXTRA_DISABLED_CATEGORIES: Record<string, readonly string[]> =
  {
    company: ['industry'],
    industry: ['company'],
  };

function getDisabledCategories(unitOfAnalysis: string): string[] {
  const normalized = unitOfAnalysis.toLowerCase();
  const extras = UNIT_EXTRA_DISABLED_CATEGORIES[normalized] ?? [];
  return [normalized, ...extras];
}

export function getDisabledCategoryLabels(
  unitOfAnalysis: string | null
): string[] {
  if (unitOfAnalysis === null || unitOfAnalysis === '') {
    return [];
  }

  return getDisabledCategories(unitOfAnalysis).map((c) => startCase(c));
}

export function getDisabledAggregationKeys(
  unitOfAnalysis: string | null,
  aggregationItems: AggregationColumnItem[]
): Set<string> {
  if (unitOfAnalysis === null || unitOfAnalysis === '') {
    return new Set();
  }

  const categorySet = new Set(getDisabledCategories(unitOfAnalysis));
  const keys = new Set<string>();

  for (const item of aggregationItems) {
    if (item.type === 'column') {
      if (categorySet.has(item.option.key.toLowerCase())) {
        keys.add(item.option.key);
      }
      continue;
    }

    if (categorySet.has(item.name.toLowerCase())) {
      for (const opt of item.options) {
        keys.add(opt.key);
      }
    }
  }

  return keys;
}

export function isAggregationGroupFullyDisabled(
  options: { key: string }[],
  disabledColumnKeys: Set<string>
): boolean {
  if (options.length === 0) {
    return false;
  }
  return options.every((opt) => disabledColumnKeys.has(opt.key));
}
